import { useEffect } from "react";

const PRODUCTION_DOMAIN = "https://blacklineestimating.com";
const PRODUCTION_HOSTNAME = "blacklineestimating.com";

// Check if we're on the production domain
function isProductionDomain() {
  if (typeof window === "undefined") return false;
  const hostname = window.location.hostname;
  return hostname === PRODUCTION_HOSTNAME || hostname === `www.${PRODUCTION_HOSTNAME}`;
}

// Check if we're on a preview domain
function isPreviewDomain() {
  if (typeof window === "undefined") return true; // Default to preview for SSR safety
  const hostname = window.location.hostname;
  return hostname.includes(".preview.emergentagent.com") || hostname === "localhost";
}

const pageMeta = {
  home: {
    title: "Blackline Estimating | Construction Cost Engineering & Tender Assurance | NZ & AU",
    description: "Independent construction cost engineering and tender assurance for small residential and civil contractors in New Zealand and Australia. Pre-submission tender review, estimate development, cost intelligence. NZ$595 / A$545 excl. GST.",
    canonical: PRODUCTION_DOMAIN,
  },
  tenderRedReview: {
    title: "Tender Red Review | Independent Pre-Submission Tender Review | Blackline",
    description: "Independent pre-submission construction tender review. Fresh eyes before the number goes out. Scope gaps, calculation errors, quote exclusions. NZ$595 / A$545 excl. GST.",
    canonical: `${PRODUCTION_DOMAIN}/tender-red-review`,
  },
  services: {
    title: "Services | Construction Cost Engineering & Tender Assurance | Blackline",
    description: "Construction cost engineering services: Tender Red Review, Cost Engineering, Estimate Development, Cost Intelligence. For small residential and civil projects in NZ & AU.",
    canonical: `${PRODUCTION_DOMAIN}/services`,
  },
  about: {
    title: "About | Construction Cost Engineering | Blackline Estimating",
    description: "About Blackline Estimating. Independent construction cost engineering and tender assurance for small builders and civil contractors in New Zealand and Australia.",
    canonical: `${PRODUCTION_DOMAIN}/about`,
  },
  insights: {
    title: "Insights | Construction Estimating Tips & Guides | Blackline",
    description: "Construction estimating insights, tips, and guides for builders and contractors. Learn from industry expertise.",
    canonical: `${PRODUCTION_DOMAIN}/blog`,
  },
  privacy: {
    title: "Privacy Policy | Blackline Estimating",
    description: "Privacy Policy for Blackline Estimating. How we collect, use, and protect your personal information.",
    canonical: `${PRODUCTION_DOMAIN}/privacy`,
  },
  terms: {
    title: "Terms of Service | Blackline Estimating",
    description: "Terms of Service for Blackline Estimating tender review and cost engineering services.",
    canonical: `${PRODUCTION_DOMAIN}/terms`,
  },
  confidentiality: {
    title: "Confidentiality & Document Handling | Blackline Estimating",
    description: "How Blackline Estimating handles your confidential tender documents, estimates, and quotations securely.",
    canonical: `${PRODUCTION_DOMAIN}/confidentiality`,
  },
  priceDesk: {
    title: "Price Desk | Managed Supplier Pricing | Blackline Estimating",
    description: "Blackline Price Desk handles supplier quotes, RFQs, follow-ups and comparisons so you can focus on the estimate and submission. For NZ & AU contractors.",
    canonical: `${PRODUCTION_DOMAIN}/price-desk`,
  },
  notFound: {
    title: "Page Not Found | Blackline Estimating",
    description: "The page you're looking for doesn't exist. Navigate back to Blackline Estimating.",
    canonical: null, // No canonical for 404 pages
    noindex: true, // Always noindex for 404
  },
};

export function usePageMeta(page, article = null) {
  useEffect(() => {
    const meta = article
      ? {
          title: `${article.title} | Blackline Insights`,
          description: article.excerpt || pageMeta.insights.description,
          canonical: `${PRODUCTION_DOMAIN}/blog/${article.slug}`,
        }
      : pageMeta[page] || pageMeta.home;

    // Set document title
    document.title = meta.title;

    // Update or create meta description
    let descMeta = document.querySelector('meta[name="description"]');
    if (!descMeta) {
      descMeta = document.createElement("meta");
      descMeta.name = "description";
      document.head.appendChild(descMeta);
    }
    descMeta.content = meta.description;

    // Handle canonical link - remove for 404 pages
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (meta.canonical) {
      if (!canonicalLink) {
        canonicalLink = document.createElement("link");
        canonicalLink.rel = "canonical";
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.href = meta.canonical;
    } else if (canonicalLink) {
      // Remove canonical link for 404 pages
      canonicalLink.remove();
    }

    // Handle robots meta based on environment
    let robotsMeta = document.querySelector('meta[name="robots"]');
    
    // Determine the correct robots value
    let robotsContent;
    if (meta.noindex) {
      // 404 pages are always noindex, follow
      robotsContent = "noindex, follow";
    } else if (isProductionDomain()) {
      // Production: index, follow for normal pages
      robotsContent = "index, follow";
    } else {
      // Preview/localhost: noindex, nofollow
      robotsContent = "noindex, nofollow";
    }
    
    // Create or update robots meta
    if (!robotsMeta) {
      robotsMeta = document.createElement("meta");
      robotsMeta.name = "robots";
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.content = robotsContent;

    // Update Open Graph tags (skip for 404 on preview)
    if (!meta.noindex || isProductionDomain()) {
      const ogTags = {
        "og:title": meta.title,
        "og:description": meta.description,
        "og:url": meta.canonical || PRODUCTION_DOMAIN,
        "og:type": "website",
        "og:site_name": "Blackline Estimating",
      };

      Object.entries(ogTags).forEach(([property, content]) => {
        let ogMeta = document.querySelector(`meta[property="${property}"]`);
        if (!ogMeta) {
          ogMeta = document.createElement("meta");
          ogMeta.setAttribute("property", property);
          document.head.appendChild(ogMeta);
        }
        ogMeta.content = content;
      });

      // Update Twitter tags
      const twitterTags = {
        "twitter:card": "summary_large_image",
        "twitter:title": meta.title,
        "twitter:description": meta.description,
      };

      Object.entries(twitterTags).forEach(([name, content]) => {
        let twitterMeta = document.querySelector(`meta[name="${name}"]`);
        if (!twitterMeta) {
          twitterMeta = document.createElement("meta");
          twitterMeta.name = name;
          document.head.appendChild(twitterMeta);
        }
        twitterMeta.content = content;
      });
    }

    // Cleanup not needed as we're updating existing tags
  }, [page, article]);
}

export default usePageMeta;
